'use client';

import { ScrollArea } from '@mds/ui';
import {
  Drawer,
  drawerClasses,
  DrawerProps,
  IconButton,
  IconButtonProps,
  Slide,
  SlideProps,
  styled,
} from '@mui/material';
import { ComponentProps, CSSProperties, forwardRef, ReactNode } from 'react';
import { SetOptional } from 'type-fest';
import { AccessoryDrawerKey } from '../../utils/types';
import { appBarHeightCompact, appBarHeightWide } from '../AppBarLayout/AppBarLayout';
import { useAppLayout } from '../AppLayoutProvider';

declare module '@mui/material/Drawer' {
  interface DrawerTransitionSlotPropsOverrides {
    direction?: SlideProps['direction'];
  }
}

export interface AccessoryDrawerButtonOwnProps {
  /**
   * Unique accessory drawer identifier.
   *
   * Override the `AccessoryDrawers` interface to add a new drawer.
   *
   * ```ts
   * declare module '../../utils/types' {
   *   interface AccessoryDrawers {
   *     notifications: true;
   *   }
   * }
   * ```
   */
  drawerKey: AccessoryDrawerKey;
  /**
   * Icon from @mds/icons
   */
  icon: ReactNode;
  /**
   * E.g. "Open notifications"
   */
  label: string;
}

export type AccessoryDrawerButtonProps = AccessoryDrawerButtonOwnProps & Omit<IconButtonProps, 'children'>;

/**
 * Button to toggle a keyed accessory drawer.
 *
 * It should be placed in app bar actions:
 * `<AppBarLayout actions={<AccessoryDrawerButton />} />`
 */
export const AccessoryDrawerButton = forwardRef<HTMLButtonElement, AccessoryDrawerButtonProps>(
  function AccessoryDrawerButton(props, ref) {
    const { drawerKey, icon, label, ...rest } = props;
    const { openedAccessoryDrawer, toggleAccessoryDrawer } = useAppLayout();
    return (
      <IconButton
        id={`accessory-drawer-${drawerKey}-trigger`}
        color="neutral"
        aria-label={label}
        aria-controls={`accessory-drawer-${drawerKey}`}
        aria-expanded={openedAccessoryDrawer === drawerKey}
        onClick={() => {
          toggleAccessoryDrawer(drawerKey);
        }}
        {...rest}
        ref={ref}
      >
        {icon}
      </IconButton>
    );
  },
);

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: theme.zIndex.accessoryDrawer,

  [`& .${drawerClasses.paper}`]: {
    width: 'var(--app-drawer-width)',
    maxWidth: '100dvw',
    overflow: 'hidden',
    boxSizing: 'border-box',
    paddingTop: `var(--app-bar-size, ${appBarHeightCompact}px)`,
    [theme.breakpoints.up('sm')]: {
      paddingTop: `var(--app-bar-size, ${appBarHeightWide}px)`,
    },
  },
}));

export interface AccessoryDrawerOwnProps {
  /**
   * Unique accessory drawer identifier.
   *
   * Override the `AccessoryDrawers` interface to add a new drawer.
   *
   * ```ts
   * declare module '../../utils/types' {
   *   interface AccessoryDrawers {
   *     notifications: true;
   *   }
   * }
   * ```
   */
  drawerKey: AccessoryDrawerKey;
  /**
   * Sidebar content scrolls by default.
   *
   * If the content is more complex, e.g. has sticky sections or multiple scroll areas,
   * set this flag to true and use `<ScrollArea>` to configure scrollable sections
   * manually.
   */
  disableScrollArea?: boolean;
  /**
   * Props to be passed to the drawer slots.
   */
  slotProps?: DrawerProps['slotProps'] & {
    /**
     * ScrollArea.Content props.
     *
     * To further customise the scroll area, use disableScrollArea and define it manually.
     */
    content?: ComponentProps<typeof ScrollArea.Content>;
  };
}

export type AccessoryDrawerProps = SetOptional<DrawerProps, 'open' | 'onClose'> & AccessoryDrawerOwnProps;

type DrawerSlotProps = NonNullable<DrawerProps['slotProps']>;

function getAnchorSlideDirection(anchor: NonNullable<DrawerProps['anchor']>): SlideProps['direction'] {
  switch (anchor) {
    case 'left':
      return 'right';
    case 'top':
      return 'down';
    case 'bottom':
      return 'up';
    case 'right':
    default:
      return 'left';
  }
}

const AccessoryDrawerSlide = forwardRef<unknown, SlideProps>(function AccessoryDrawerSlide(
  { direction, ...props },
  ref,
) {
  return <Slide {...props} ref={ref} direction={direction} />;
});

/**
 * Accessory drawer.
 *
 * Opening the drawer closes all other accessory drawers, like the account menu or app
 * switcher.
 *
 * Drawer is unmounted when closed by default.
 */
export function AccessoryDrawer({
  drawerKey,
  children,
  disableScrollArea,
  slotProps,
  slots,
  anchor = 'right',
  ...rest
}: AccessoryDrawerProps) {
  const { appBarSize, openedAccessoryDrawer, toggleAccessoryDrawer } = useAppLayout();
  const {
    content: contentSlotProps,
    transition: transitionSlotProps,
    paper: paperSlotProps,
    ...drawerSlotProps
  } = slotProps ?? {};
  const mergedSlotProps: DrawerSlotProps = {
    ...drawerSlotProps,
    // MUI renders the dialog role on the Paper slot, not the Drawer/Modal root, so the
    // accessible name reference has to live there for the open drawer to have a name.
    paper: {
      'aria-labelledby': `accessory-drawer-${drawerKey}-trigger`,
      ...paperSlotProps,
    },
    transition: {
      direction: getAnchorSlideDirection(anchor),
      ...transitionSlotProps,
    },
  };

  return (
    <StyledDrawer
      open={openedAccessoryDrawer === drawerKey}
      onClose={() => toggleAccessoryDrawer(drawerKey, false)}
      anchor={anchor}
      variant="temporary"
      id={`accessory-drawer-${drawerKey}`}
      ModalProps={{
        keepMounted: false,
      }}
      slots={{
        ...slots,
        transition: AccessoryDrawerSlide,
      }}
      slotProps={mergedSlotProps}
      {...rest}
      style={
        {
          '--app-drawer-width': '360px',
          ...(appBarSize ? { '--app-bar-size': `${appBarSize}px` } : {}),
          ...rest.style,
        } as CSSProperties
      }
    >
      {disableScrollArea && children}

      {!disableScrollArea && (
        <ScrollArea.Root sx={{ flex: 1, overflow: 'hidden', width: '100%' }}>
          <ScrollArea.Viewport>
            <ScrollArea.Content {...contentSlotProps}>{children}</ScrollArea.Content>
          </ScrollArea.Viewport>
        </ScrollArea.Root>
      )}
    </StyledDrawer>
  );
}
